// Where an SHA claim is, and what may move it (docs/plans/phase1-sha.md §4.8, §6.4;
// checklist 26, 27). The states are Wodi's. SHA's own status values reach this module only
// as our outcome buckets, mapped by each adapter from a table taken from the specification
// (TODO(verify-spec): B8). A status that would move a claim backwards is reported for a
// person, never applied silently.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';

export const CLAIM_STATES = [
  'draft',
  'ready',
  'queued',
  'send_failed',
  'submitted',
  'in_review',
  'returned',
  'rejected',
  'partly_approved',
  'approved',
  'paid',
  'written_off',
  'entered_in_error',
] as const;
export type ClaimState = (typeof CLAIM_STATES)[number];

export const FINAL_CLAIM_STATES: readonly ClaimState[] = [
  'paid',
  'written_off',
  'entered_in_error',
];

export const CLAIM_STATUS_OUTCOMES = [
  'pending',
  'approved',
  'partly_approved',
  'rejected',
  'returned',
  'paid',
  'unknown',
] as const;
export type ClaimStatusOutcome = (typeof CLAIM_STATUS_OUTCOMES)[number];

export const CLAIM_EVENTS = [
  'mark_ready',
  'mark_draft',
  'queue',
  'send_ok',
  'send_failed',
  'status',
  'resubmit',
  'write_off_approved',
  'enter_in_error',
] as const;

export type ClaimEvent =
  | { readonly kind: Exclude<(typeof CLAIM_EVENTS)[number], 'status'> }
  | { readonly kind: 'status'; readonly outcome: ClaimStatusOutcome };

export type ClaimTransitionError =
  'claim.not_allowed' | 'claim.not_submitted' | 'claim.final' | 'claim.status_regressed';

export type ClaimTransition =
  | { readonly ok: true; readonly state: ClaimState; readonly unknownStatus?: true }
  | { readonly ok: false; readonly code: ClaimTransitionError };

/** How far along the claim is; SHA's answers may only move it forward. */
const RANK: Record<ClaimState, number> = {
  draft: 0,
  ready: 1,
  queued: 2,
  send_failed: 2,
  submitted: 3,
  in_review: 4,
  returned: 5,
  rejected: 5,
  partly_approved: 6,
  approved: 6,
  paid: 7,
  written_off: 8,
  entered_in_error: 8,
};

const STATUS_TARGET: Record<Exclude<ClaimStatusOutcome, 'unknown'>, ClaimState> = {
  pending: 'in_review',
  approved: 'approved',
  partly_approved: 'partly_approved',
  rejected: 'rejected',
  returned: 'returned',
  paid: 'paid',
};

const SENT: readonly ClaimState[] = [
  'submitted',
  'in_review',
  'returned',
  'rejected',
  'partly_approved',
  'approved',
  'paid',
];

function from(
  state: ClaimState,
  allowed: readonly ClaimState[],
  to: ClaimState,
): Result<ClaimState, ClaimTransitionError> {
  if (allowed.includes(state)) return ok(to);
  return err(FINAL_CLAIM_STATES.includes(state) ? 'claim.final' : 'claim.not_allowed');
}

function answer(result: Result<ClaimState, ClaimTransitionError>): ClaimTransition {
  return result.ok ? { ok: true, state: result.value } : { ok: false, code: result.code };
}

export function claimTransition(state: ClaimState, event: ClaimEvent): ClaimTransition {
  switch (event.kind) {
    case 'mark_ready':
      return answer(from(state, ['draft', 'ready'], 'ready'));
    case 'mark_draft':
      return answer(from(state, ['draft', 'ready'], 'draft'));
    case 'queue':
      return answer(from(state, ['ready'], 'queued'));
    case 'send_ok':
      return answer(from(state, ['queued'], 'submitted'));
    case 'send_failed':
      return answer(from(state, ['queued'], 'send_failed'));
    case 'resubmit':
      return answer(from(state, ['draft', 'send_failed', 'rejected', 'returned'], 'queued'));
    case 'write_off_approved':
      return answer(
        from(state, ['send_failed', 'rejected', 'returned', 'partly_approved'], 'written_off'),
      );
    case 'enter_in_error':
      return answer(
        from(state, ['draft', 'ready', 'send_failed', 'rejected', 'returned'], 'entered_in_error'),
      );
    case 'status': {
      if (state === 'written_off' || state === 'entered_in_error') {
        return { ok: false, code: 'claim.final' };
      }
      if (!SENT.includes(state)) return { ok: false, code: 'claim.not_submitted' };
      if (event.outcome === 'unknown') return { ok: true, state, unknownStatus: true };
      const target = STATUS_TARGET[event.outcome];
      if (target === state) return { ok: true, state };
      if (state === 'paid' || RANK[target] < RANK[state]) {
        return { ok: false, code: 'claim.status_regressed' };
      }
      return { ok: true, state: target };
    }
  }
}
